import { useState, useEffect, useRef, useCallback } from 'react';
import createGlobe from 'cobe';
import { getCoords } from '../../utils/airportCoords';
import { projectToScreen, greatCirclePoints, phiForLongitude } from '../../utils/mapProjection';
import { Crosshair } from 'lucide-react';
import type { Temporal } from '../../utils/flightStats';

interface RouteMapProps {
  routes: { from: string; to: string; count: number; temporal?: Temporal }[];
  airports: (string | { iata: string; temporal?: Temporal })[];
  baseAirport: string;
  snapshotRef?: React.MutableRefObject<(() => Promise<Blob>) | null>;
}

const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

export function RouteMap({ routes, airports, baseAirport, snapshotRef }: RouteMapProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const arcCanvasRef = useRef<HTMLCanvasElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const pointerDown = useRef(false);
  const pointerX = useRef(0);
  const [isZoomed, setIsZoomed] = useState(false);

  // Zoom refs — mutated in animation loop, no re-renders needed
  const zoomRef = useRef(1);
  const targetZoomRef = useRef(1);

  // Rotation refs — exposed so reset button can access
  const targetPhiRef = useRef(0);
  const currentPhiRef = useRef(0);

  const resolvedAirports = airports
    .map((ap) => {
      const iata = typeof ap === 'string' ? ap : ap.iata;
      const temporal = typeof ap === 'string' ? undefined : ap.temporal;
      return { iata, temporal, coords: getCoords(iata) };
    })
    .filter((a): a is { iata: string; temporal: Temporal | undefined; coords: { lat: number; lon: number } } => a.coords !== null);

  const resolvedRoutes = routes
    .map((r) => ({
      ...r,
      fromCoords: getCoords(r.from),
      toCoords: getCoords(r.to),
    }))
    .filter(
      (r): r is typeof r & { fromCoords: { lat: number; lon: number }; toCoords: { lat: number; lon: number } } =>
        r.fromCoords !== null && r.toCoords !== null,
    );

  const baseCoords = getCoords(baseAirport);
  const initialPhi = baseCoords ? phiForLongitude(baseCoords.lon) : -Math.PI / 2;
  const initialTheta = baseCoords ? Math.max(-0.5, Math.min(0.5, (baseCoords.lat * Math.PI) / 180 * 0.4)) : 0.2;

  // Only label route endpoints
  const labelAirports = new Set<string>();
  for (const r of routes) {
    labelAirports.add(r.from);
    labelAirports.add(r.to);
  }

  // Store overlay data in refs so drawOverlay always uses latest without recreating globe
  const resolvedAirportsRef = useRef(resolvedAirports);
  resolvedAirportsRef.current = resolvedAirports;
  const resolvedRoutesRef = useRef(resolvedRoutes);
  resolvedRoutesRef.current = resolvedRoutes;
  const labelAirportsRef = useRef(labelAirports);
  labelAirportsRef.current = labelAirports;

  const drawOverlay = useCallback(
    (canvas: HTMLCanvasElement, phi: number, theta: number, size: number) => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const dpr = window.devicePixelRatio || 1;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // --- Arcs: glow layer + solid line ---
      for (const route of resolvedRoutesRef.current) {
        const points = greatCirclePoints(
          route.fromCoords.lat, route.fromCoords.lon,
          route.toCoords.lat, route.toCoords.lon,
          80,
        );

        // Build visible segments
        const segments: { x: number; y: number }[][] = [];
        let current: { x: number; y: number }[] = [];
        for (const p of points) {
          const { x, y, visible } = projectToScreen(p.lat, p.lon, phi, theta, size);
          if (visible) {
            current.push({ x: x * dpr, y: y * dpr });
          } else if (current.length > 0) {
            segments.push(current);
            current = [];
          }
        }
        if (current.length > 0) segments.push(current);

        // Color based on temporal
        const isFuture = route.temporal === 'future';
        const glowColor = isFuture ? 'rgba(100, 180, 220, 0.2)' : 'rgba(220, 80, 70, 0.25)';
        const solidColor = isFuture ? 'rgba(100, 180, 220, 0.6)' : 'rgba(220, 80, 70, 0.95)';

        for (const seg of segments) {
          if (seg.length < 2) continue;

          // Glow
          ctx.beginPath();
          ctx.moveTo(seg[0].x, seg[0].y);
          for (let i = 1; i < seg.length; i++) ctx.lineTo(seg[i].x, seg[i].y);
          ctx.strokeStyle = glowColor;
          ctx.lineWidth = 6 * dpr;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.stroke();

          // Solid line
          ctx.beginPath();
          ctx.moveTo(seg[0].x, seg[0].y);
          for (let i = 1; i < seg.length; i++) ctx.lineTo(seg[i].x, seg[i].y);
          ctx.strokeStyle = solidColor;
          ctx.lineWidth = 2 * dpr;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.stroke();
        }
      }

      // --- Airport dots + labels with de-overlap ---
      const placedLabels: { x: number; y: number }[] = [];

      for (const ap of resolvedAirportsRef.current) {
        const { x, y, visible } = projectToScreen(ap.coords.lat, ap.coords.lon, phi, theta, size);
        if (!visible) continue;

        const px = x * dpr;
        const py = y * dpr;
        const isBase = ap.iata === baseAirport;
        const isFutureOnly = ap.temporal === 'future';
        const dotR = (isBase ? 5 : isFutureOnly ? 2 : 2.5) * dpr;

        // Base airport pulse ring
        if (isBase) {
          ctx.beginPath();
          ctx.arc(px, py, dotR + 3 * dpr, 0, Math.PI * 2);
          ctx.strokeStyle = 'rgba(220, 80, 70, 0.3)';
          ctx.lineWidth = 1.5 * dpr;
          ctx.stroke();
        }

        // Dot fill — future-only airports get blue color
        if (isFutureOnly) {
          ctx.beginPath();
          ctx.arc(px, py, dotR, 0, Math.PI * 2);
          ctx.strokeStyle = 'rgba(100, 180, 220, 0.7)';
          ctx.lineWidth = 1.5 * dpr;
          ctx.stroke();
        } else {
          ctx.beginPath();
          ctx.arc(px, py, dotR, 0, Math.PI * 2);
          ctx.fillStyle = isBase ? '#dc5046' : 'rgba(220, 80, 70, 0.7)';
          ctx.fill();
        }

        // Label — only if route endpoint and enough distance from other labels
        if (labelAirportsRef.current.has(ap.iata)) {
          const tooClose = placedLabels.some(
            (l) => Math.hypot(l.x - px, l.y - py) < 40 * dpr,
          );
          if (!tooClose) {
            ctx.font = `600 ${10 * dpr}px "DM Sans", sans-serif`;
            ctx.fillStyle = 'rgba(255, 200, 190, 0.9)';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            ctx.fillText(ap.iata, px + dotR + 4 * dpr, py);
            placedLabels.push({ x: px, y: py });
          }
        }
      }
    },
    [baseAirport],
  );

  // Reset handler — smoothly animate back to initial position and zoom
  const handleReset = useCallback(() => {
    targetPhiRef.current = initialPhi;
    targetZoomRef.current = 1;
  }, [initialPhi]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const arcCanvas = arcCanvasRef.current;
    if (!canvas || !arcCanvas) return;

    const container = canvas.parentElement;
    if (!container) return;

    const size = container.clientWidth;
    const dpr = window.devicePixelRatio || 1;
    arcCanvas.width = size * dpr;
    arcCanvas.height = size * dpr;
    arcCanvas.style.width = `${size}px`;
    arcCanvas.style.height = `${size}px`;

    const markers = resolvedAirports.map((a) => ({
      location: [a.coords.lat, a.coords.lon] as [number, number],
      size: a.iata === baseAirport ? 0.04 : 0.015,
    }));

    currentPhiRef.current = initialPhi;
    targetPhiRef.current = initialPhi;

    // Scale dot density with DPR — fewer samples on low-res screens prevents moiré
    const samplesForDpr = dpr >= 2 ? 40000 : dpr >= 1.5 ? 28000 : 18000;
    // Base brightness fills land slightly on low-DPR, smoothing dot aliasing
    const baseBrightnessForDpr = dpr >= 2 ? 0 : 0.07;

    let prevZoomed = false;

    let globe: ReturnType<typeof createGlobe>;
    try {
      globe = createGlobe(canvas, {
      devicePixelRatio: dpr,
      width: size * dpr,
      height: size * dpr,
      phi: initialPhi,
      theta: initialTheta,
      dark: 1,
      diffuse: 3,
      mapSamples: samplesForDpr,
      mapBrightness: 8,
      mapBaseBrightness: baseBrightnessForDpr,
      baseColor: [0.4, 0.33, 0.25],
      markerColor: [0.86, 0.31, 0.27],
      glowColor: [0.15, 0.18, 0.25],
      markers,
      scale: 1,
      offset: [0, 0],
      onRender: (state) => {
        if (!pointerDown.current) {
          targetPhiRef.current -= 0.002;
        }
        currentPhiRef.current += (targetPhiRef.current - currentPhiRef.current) * 0.1;
        state.phi = currentPhiRef.current;
        state.theta = initialTheta;

        // Smooth zoom interpolation
        zoomRef.current += (targetZoomRef.current - zoomRef.current) * 0.15;
        if (innerRef.current) {
          innerRef.current.style.transform = `scale(${zoomRef.current})`;
        }

        // Update isZoomed state (throttled — only when crossing threshold)
        const nowZoomed = zoomRef.current > 1.05;
        if (nowZoomed !== prevZoomed) {
          prevZoomed = nowZoomed;
          setIsZoomed(nowZoomed);
        }

        drawOverlay(arcCanvas, currentPhiRef.current, initialTheta, size);
      },
    });
    } catch {
      return;
    }

    // Expose snapshot function
    if (snapshotRef) {
      snapshotRef.current = () =>
        new Promise<Blob>((resolve, reject) => {
          const SNAP_SIZE = 1200;
          const snapCanvas = document.createElement('canvas');
          snapCanvas.width = SNAP_SIZE;
          snapCanvas.height = SNAP_SIZE;
          snapCanvas.style.cssText = 'position:fixed;top:-9999px;left:-9999px;pointer-events:none';
          document.body.appendChild(snapCanvas);

          let snapGlobe: ReturnType<typeof createGlobe>;
          try {
            snapGlobe = createGlobe(snapCanvas, {
              devicePixelRatio: 1,
              width: SNAP_SIZE,
              height: SNAP_SIZE,
              phi: initialPhi,
              theta: initialTheta,
              dark: 1,
              diffuse: 3,
              mapSamples: 80000,
              mapBrightness: 8,
              mapBaseBrightness: 0.05,
              baseColor: [0.4, 0.33, 0.25],
              markerColor: [0.86, 0.31, 0.27],
              glowColor: [0.15, 0.18, 0.25],
              markers,
              scale: 1,
              offset: [0, 0],
              context: { preserveDrawingBuffer: true },
              onRender: (state) => {
                state.phi = initialPhi;
                state.theta = initialTheta;
              },
            });
          } catch {
            document.body.removeChild(snapCanvas);
            return reject(new Error('WebGL unavailable'));
          }

          requestAnimationFrame(() =>
            requestAnimationFrame(() => {
              const overlay = document.createElement('canvas');
              overlay.width = SNAP_SIZE;
              overlay.height = SNAP_SIZE;
              const octx = overlay.getContext('2d');
              if (octx) {
                octx.imageSmoothingEnabled = true;
                octx.imageSmoothingQuality = 'high';
                for (const route of resolvedRoutesRef.current) {
                  const points = greatCirclePoints(
                    route.fromCoords.lat, route.fromCoords.lon,
                    route.toCoords.lat, route.toCoords.lon, 80,
                  );
                  const segs: { x: number; y: number }[][] = [];
                  let cur: { x: number; y: number }[] = [];
                  for (const p of points) {
                    const { x, y, visible } = projectToScreen(p.lat, p.lon, initialPhi, initialTheta, SNAP_SIZE);
                    if (visible) cur.push({ x, y });
                    else if (cur.length) { segs.push(cur); cur = []; }
                  }
                  if (cur.length) segs.push(cur);
                  const snapFuture = route.temporal === 'future';
                  const snapGlow = snapFuture ? 'rgba(100, 180, 220, 0.3)' : 'rgba(220, 80, 70, 0.35)';
                  const snapSolid = snapFuture ? 'rgba(100, 180, 220, 0.8)' : 'rgba(220, 80, 70, 1.0)';
                  for (const seg of segs) {
                    if (seg.length < 2) continue;
                    // Thick glow
                    octx.beginPath();
                    octx.moveTo(seg[0].x, seg[0].y);
                    for (let i = 1; i < seg.length; i++) octx.lineTo(seg[i].x, seg[i].y);
                    octx.strokeStyle = snapGlow;
                    octx.lineWidth = 24;
                    octx.lineCap = 'round';
                    octx.lineJoin = 'round';
                    octx.stroke();
                    // Solid core
                    octx.beginPath();
                    octx.moveTo(seg[0].x, seg[0].y);
                    for (let i = 1; i < seg.length; i++) octx.lineTo(seg[i].x, seg[i].y);
                    octx.strokeStyle = snapSolid;
                    octx.lineWidth = 8;
                    octx.lineCap = 'round';
                    octx.lineJoin = 'round';
                    octx.stroke();
                  }
                }
                const placed: { x: number; y: number }[] = [];
                for (const ap of resolvedAirportsRef.current) {
                  const { x, y, visible } = projectToScreen(ap.coords.lat, ap.coords.lon, initialPhi, initialTheta, SNAP_SIZE);
                  if (!visible) continue;
                  const isBase = ap.iata === baseAirport;
                  const snapFutureOnly = ap.temporal === 'future';
                  const dotR = isBase ? 16 : snapFutureOnly ? 8 : 10;
                  if (isBase) {
                    octx.beginPath();
                    octx.arc(x, y, dotR + 8, 0, Math.PI * 2);
                    octx.strokeStyle = 'rgba(220, 80, 70, 0.4)';
                    octx.lineWidth = 4;
                    octx.stroke();
                  }
                  if (snapFutureOnly) {
                    octx.beginPath();
                    octx.arc(x, y, dotR, 0, Math.PI * 2);
                    octx.strokeStyle = 'rgba(100, 180, 220, 0.8)';
                    octx.lineWidth = 3;
                    octx.stroke();
                  } else {
                    octx.beginPath();
                    octx.arc(x, y, dotR, 0, Math.PI * 2);
                    octx.fillStyle = isBase ? '#dc5046' : 'rgba(220, 80, 70, 0.9)';
                    octx.fill();
                  }
                  if (labelAirportsRef.current.has(ap.iata)) {
                    const tooClose = placed.some((l) => Math.hypot(l.x - x, l.y - y) < 80);
                    if (!tooClose) {
                      octx.font = '700 40px "DM Sans", sans-serif';
                      octx.fillStyle = 'rgba(255, 220, 210, 1.0)';
                      octx.textAlign = 'left';
                      octx.textBaseline = 'middle';
                      octx.fillText(ap.iata, x + dotR + 10, y);
                      placed.push({ x, y });
                    }
                  }
                }
              }

              const out = document.createElement('canvas');
              out.width = SNAP_SIZE;
              out.height = SNAP_SIZE;
              const outCtx = out.getContext('2d')!;
              outCtx.drawImage(snapCanvas, 0, 0);
              outCtx.drawImage(overlay, 0, 0);

              snapGlobe.destroy();
              document.body.removeChild(snapCanvas);

              out.toBlob(
                (blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
                'image/png',
              );
            }),
          );
        });
    }

    // --- Pointer: drag to rotate ---
    const onPointerDown = (e: PointerEvent) => {
      pointerDown.current = true;
      pointerX.current = e.clientX;
    };
    const onPointerUp = () => {
      pointerDown.current = false;
    };
    const onPointerMove = (e: PointerEvent) => {
      if (!pointerDown.current) return;
      const dx = e.clientX - pointerX.current;
      pointerX.current = e.clientX;
      targetPhiRef.current += dx * 0.005;
    };

    // --- Wheel: scroll to zoom ---
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      targetZoomRef.current = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, targetZoomRef.current - e.deltaY * 0.003));
    };

    // --- Pinch: two-finger zoom ---
    let lastPinchDist = 0;
    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        lastPinchDist = Math.hypot(dx, dy);
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.hypot(dx, dy);
        const delta = dist - lastPinchDist;
        lastPinchDist = dist;
        targetZoomRef.current = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, targetZoomRef.current + delta * 0.008));
      }
    };

    container.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerUp);
    window.addEventListener('pointermove', onPointerMove);
    container.addEventListener('wheel', onWheel, { passive: false });
    container.addEventListener('touchstart', onTouchStart, { passive: true });
    container.addEventListener('touchmove', onTouchMove, { passive: false });

    return () => {
      if (snapshotRef) snapshotRef.current = null;
      globe?.destroy();
      container.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerUp);
      window.removeEventListener('pointermove', onPointerMove);
      container.removeEventListener('wheel', onWheel);
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseAirport]);

  return (
    <div className="relative w-full overflow-hidden rounded-2xl" style={{ aspectRatio: '1' }}>
      <div ref={innerRef} className="w-full h-full origin-center" style={{ touchAction: 'pan-y' }}>
        <canvas ref={canvasRef} className="w-full h-full" style={{ contain: 'layout size' }} />
        <canvas ref={arcCanvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />
      </div>
      {/* Reset button — only visible when zoomed */}
      {isZoomed && (
        <button
          onClick={handleReset}
          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-slate-800/80 backdrop-blur-sm border border-slate-600/50 flex items-center justify-center text-slate-300 hover:text-white hover:bg-slate-700/80 transition-all"
          title="Reset view"
        >
          <Crosshair size={14} />
        </button>
      )}
    </div>
  );
}
